/**
 * WHERE DID THIS ACCOUNT COME FROM — captured silently, nothing is ever asked.
 *
 * One record, written on the FIRST page the visitor lands on, read back when they sign up
 * and stored on the `_User` as `acq`. Nobody sees a question and the wizard does not grow
 * a step (owner, 2026-10-17: "I dont want to ask. i want behind the scene").
 *
 * ⚠️⚠️ FIRST TOUCH, AND NEVER OVERWRITTEN. People land on a campaign link, read, wander
 * off and come back twenty minutes later by typing the name — a clean URL with nothing in
 * it. Capture at signup instead of on landing and you record almost nothing, then conclude
 * that direct traffic is everything. And if they return on a DIFFERENT campaign the first
 * record stands: otherwise last click takes credit for work first click did, and the
 * cheapest channel always looks like the best one.
 *
 * ⚠️⚠️ localStorage IS PER ORIGIN. The marketing site (plannera.app) and the app
 * (table.plannera.app, bookingsplanner.com, admin.dreamdiner.io) are DIFFERENT ORIGINS, so
 * a record written on one is invisible to the other — subdomains included. The marketing
 * site therefore decorates its outbound links (see `acqQuery`) and this module reads them
 * back here. That hand-off is the only thing that crosses.
 *
 * ⚠️ There is a TWIN of this file in SWS_Sellera_Websites_Shared/src/utils/acquisition.js.
 * They must agree on the storage key and the shape. Change one, change the other.
 *
 * Privacy: a campaign name, a referring DOMAIN (never the full URL — the page they came
 * from is their business), the path they landed on, and a timestamp. No IP, no
 * fingerprint, no third-party pixel. It lives on the account being created and is deleted
 * with it. The policy already names "referring or exit pages" in 2.3.
 */

const KEY = 'dd_acq'
/** Older than this and it is no longer the visit that brought them. */
const MAX_AGE_DAYS = 90

export interface Acq {
  /** Normalised channel: google, google_ads, youtube, instagram, referral… */
  src?: string
  /** utm_medium, when a tagged link carried one. */
  med?: string
  /** utm_campaign, or the business id for a `?ref=` referral. */
  camp?: string
  /** The referring HOST only — evidence, so a wrong rule can be re-derived later. */
  ref?: string
  /** The path first landed on: /spa, /car, /pricing. Useful even with no campaign. */
  land?: string
  /** ISO, when they first landed. */
  at?: string
}

/**
 * The normalisation table. Keep the raw host in `ref` as well, so a rule that turns out
 * wrong can be recomputed from the evidence instead of having lost it.
 *
 * ⚠️ `google.<tld>` is matched on the FIRST label, never `google.com`: most of Europe
 * arrives as google.de / google.co.uk / google.fr, and matching the full host would
 * silently drop them.
 */
function channelFromHost(host: string): string {
  const h = host.replace(/^www\./, '').toLowerCase()
  if (/^google\./.test(h) || /\.google\./.test(h)) return 'google'
  if (/(^|\.)youtube\.com$/.test(h) || h === 'youtu.be') return 'youtube'
  if (/(^|\.)instagram\.com$/.test(h)) return 'instagram'
  if (/(^|\.)facebook\.com$/.test(h) || h === 'fb.com' || /(^|\.)m\.facebook\.com$/.test(h)) return 'facebook'
  if (/(^|\.)tiktok\.com$/.test(h)) return 'tiktok'
  if (/(^|\.)linkedin\.com$/.test(h) || h === 'lnkd.in') return 'linkedin'
  if (/(^|\.)x\.com$/.test(h) || /(^|\.)twitter\.com$/.test(h) || h === 't.co') return 'x'
  if (/(^|\.)bing\.com$/.test(h)) return 'bing'
  if (/(^|\.)duckduckgo\.com$/.test(h)) return 'duckduckgo'
  if (/(^|\.)reddit\.com$/.test(h)) return 'reddit'
  if (/(^|\.)pinterest\./.test(h)) return 'pinterest'
  return ''
}

const clean = (v: string | null, max = 120) =>
  String(v || '').replace(/[\u0000-\u001F]/g, '').trim().slice(0, max)

/** Our own hosts never count as a referrer — that is internal navigation, not a source. */
function isOwnHost(host: string): boolean {
  const h = host.replace(/^www\./, '').toLowerCase()
  return /(^|\.)(plannera\.app|bookingsplanner\.com|dreamdiner\.io|sellera\.tech|shiftaurant\.com)$/.test(h)
}

/** Build a record from THIS page's URL and referrer. Null when there is nothing to say. */
function readFromPage(): Acq | null {
  try {
    const url = new URL(window.location.href)
    const p = url.searchParams
    const out: Acq = {}

    // 1. A tagged link beats everything: it is the one signal we authored ourselves.
    const utmSource = clean(p.get('utm_source'), 60)
    if (utmSource) out.src = utmSource.toLowerCase()
    const med = clean(p.get('utm_medium'), 60)
    if (med) out.med = med.toLowerCase()
    const camp = clean(p.get('utm_campaign'), 120)
    if (camp) out.camp = camp

    // 2. A business's own referral link — the only thing that turns word of mouth into
    //    data, because the owner sending it is the recommendation.
    const ref = clean(p.get('ref'), 40)
    if (ref && !out.src) { out.src = 'referral'; out.camp = out.camp || ref }

    // 3. Google Ads auto-tagging. The ONLY way to split paid from organic: both arrive
    //    with a google.<tld> referrer and are otherwise identical.
    if (p.get('gclid') || p.get('gbraid') || p.get('wbraid')) out.src = 'google_ads'

    // 4. Nothing tagged — fall back to who sent them.
    if (!out.src && document.referrer) {
      try {
        const rh = new URL(document.referrer).hostname
        if (!isOwnHost(rh)) {
          out.ref = rh.replace(/^www\./, '').toLowerCase().slice(0, 120)
          out.src = channelFromHost(rh) || 'referral'
        }
      } catch { /* a referrer that is not a URL tells us nothing */ }
    } else if (document.referrer) {
      // Tagged AND referred: keep the host as evidence beside the tag.
      try {
        const rh = new URL(document.referrer).hostname
        if (!isOwnHost(rh)) out.ref = rh.replace(/^www\./, '').toLowerCase().slice(0, 120)
      } catch { /* ignore */ }
    }

    // 5. The hand-off from the marketing site. `acqQuery` sends these three alongside the
    //    utm_*, and WITHOUT reading them back the original landing would be replaced by
    //    the app's own path and the app's own clock — losing the one thing the hand-off
    //    existed to carry.
    const handedRef = clean(p.get('dd_ref'), 120)
    const handedLand = clean(p.get('dd_land'), 120)
    const handedAt = clean(p.get('dd_at'), 40)
    if (handedRef && !out.ref) out.ref = handedRef.toLowerCase()

    out.land = handedLand || clean(url.pathname, 120) || '/'
    // Only trust a handed-over timestamp that parses and is not in the future.
    const handedTime = handedAt ? Date.parse(handedAt) : NaN
    out.at = Number.isFinite(handedTime) && handedTime <= Date.now() + 60000
      ? new Date(handedTime).toISOString()
      : new Date().toISOString()

    // A landing path and a timestamp alone are not a source. Only keep a record that
    // actually says where they came from, or the blank group stops being meaningful.
    return out.src ? out : null
  } catch {
    return null
  }
}

/** The stored record, or null. Expired records are treated as absent. */
export function readAcq(): Acq | null {
  try {
    const raw = window.localStorage.getItem(KEY)
    if (!raw) return null
    const v = JSON.parse(raw) as Acq
    if (!v || typeof v !== 'object' || !v.src) return null
    if (v.at && Date.now() - new Date(v.at).getTime() > MAX_AGE_DAYS * 864e5) return null
    return v
  } catch {
    // Private mode, blocked storage, corrupt JSON — never let this break a page.
    return null
  }
}

/**
 * Capture on landing. Called once from a boot file. Does nothing when a record already
 * exists — that is the first-touch rule, and it is the whole point.
 */
export function captureAcq(): void {
  try {
    if (readAcq()) return
    const found = readFromPage()
    if (found) window.localStorage.setItem(KEY, JSON.stringify(found))
  } catch { /* storage unavailable: carry on without attribution */ }
}

/**
 * The record as query parameters, for a link LEAVING this origin to one of our apps.
 * Used by the marketing site; harmless here, and kept so both twins stay identical.
 */
export function acqQuery(): string {
  const a = readAcq()
  if (!a) return ''
  const p = new URLSearchParams()
  if (a.src) p.set('utm_source', a.src)
  if (a.med) p.set('utm_medium', a.med)
  if (a.camp) p.set('utm_campaign', a.camp)
  if (a.ref) p.set('dd_ref', a.ref)
  if (a.land) p.set('dd_land', a.land)
  if (a.at) p.set('dd_at', a.at)
  return p.toString()
}
